import { api, type DayDetail, type Station, type Summary } from '../api.ts';
import { el, escapeHtml, formatDateFull, formatTime, nl, temp, ymd } from '../components/ui.ts';

/**
 * Lijntekeningen uit het ontwerp, op 30 × 30 en in de accentkleur.
 *
 * Ze staan hier als kale paden en niet als losse bestanden: het zijn er drie,
 * ze zijn samen kleiner dan het verzoek dat ze zou ophalen, en zo kunnen ze
 * `currentColor` erven en dus vanzelf met het thema meelopen.
 */
const ICON = {
  drop: '<path d="M12 3.5c3 4 5 6.4 5 8.8a5 5 0 0 1-10 0c0-2.4 2-4.8 5-8.8Z"/>',
  gauge: '<path d="M4 16h16"/><path d="M12 16V8"/><path d="m8.5 11.5 3.5-3.5 3.5 3.5"/><path d="M6 19h12"/>',
  low: '<path d="M12 4v13"/><path d="m7.5 12.5 4.5 4.5 4.5-4.5"/><path d="M5 20h14"/>',
} as const;

function icon(name: keyof typeof ICON): string {
  return `<span class="ico" aria-hidden="true"><svg width="30" height="30" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"
    stroke-linejoin="round">${ICON[name]}</svg></span>`;
}

/** Pijl omhoog uit een vak — het deelicoon uit het ontwerp. */
const SHARE_ICON = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 16V4"/><path
  d="m7.5 8.5 4.5-4.5 4.5 4.5"/><path d="M4 14v4a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-4"/></svg>`;

/**
 * De hero: standplaats, de actuele temperatuur groot, en het verloop van
 * vandaag als fijne lijn met zonop/-onder en de dagextremen erop gemarkeerd.
 *
 * Navy heeft hier geen kaart en geen zwevende bollen meer: de hero staat direct
 * op de pagina, die met de temperatuur en de zonstand meekleurt. Het sfeerwerk
 * dat de bollen deden doet de achtergrond nu zelf — zie background.ts.
 */
export function heroSection(
  summary: Summary,
  station: Station,
  onShare: () => void,
): HTMLElement {
  const root = el('div', 'hero rise');

  const top = el('div', 'hero-top');
  top.append(leftColumn(summary, station, onShare), rightColumn(summary));
  root.append(top);

  const curve = curveCard(summary);
  if (curve) root.append(curve);

  return root;
}

function leftColumn(summary: Summary, station: Station, onShare: () => void): HTMLElement {
  const col = el('div');

  const place = station?.city ?? 'Weerstation';
  const date = summary.today.date ? formatDateFull(summary.today.date) : '';
  col.append(el('div', 'place', `${escapeHtml(place)}${date ? ` · ${escapeHtml(date)}` : ''}`));

  // Actuele meting; valt terug op het dagmaximum als de module stilligt.
  const live = summary.now.temperature;
  const shown = live ?? summary.today.max;
  col.append(
    el(
      'div',
      'temp-now',
      `<span class="v">${shown === null ? '—' : temp(shown)}</span><span class="deg">°</span>`,
    ),
  );

  // Onder het getal staat wanneer het gemeten is; het ontwerp zet die regel in
  // kapitalen met ruime letterafstand, klein genoeg om niet mee te lezen.
  const measured = formatTime(summary.now.measuredAt);
  if (live !== null && measured) {
    col.append(el('div', 'measured-at', `gemeten om ${escapeHtml(measured)}`));
  } else if (live === null) {
    col.append(el('div', 'measured-at', 'hoogste vandaag'));
  }

  const chips = el('div', 'hero-chips');
  if (summary.heat?.ongoing) {
    chips.append(el('span', 'pill', `Hittegolf, dag ${summary.heat.days}`));
  } else if (live !== null && summary.now.stale) {
    const at = formatTime(summary.now.measuredAt);
    chips.append(el('span', 'pill', `Laatste meting${at ? ` om ${escapeHtml(at)}` : ''}`));
  } else if (summary.anomaly.value !== null) {
    const v = summary.anomaly.value;
    chips.append(
      el('span', 'pill', `${v >= 0 ? '+' : '−'}${nl(Math.abs(v))} °C ${v >= 0 ? 'boven' : 'onder'} normaal`),
    );
  }

  /*
   * In Navy is de deelknop een rond icoon van 38 pixels. De tekst die er eerst
   * in stond zit nu in title en aria-label: zichtbaar bij het aanwijzen,
   * voorleesbaar voor wie de knop niet ziet.
   */
  const share = el('button', 'btn-ghost btn-icon', SHARE_ICON);
  share.type = 'button';
  share.title = 'Deel als afbeelding';
  share.setAttribute('aria-label', 'Deel als afbeelding');
  share.addEventListener('click', onShare);
  chips.append(share);
  col.append(chips);

  // Drie kerncijfers. Ontbreekt een sensor, dan valt die metric gewoon weg.
  const metrics = el('div', 'metrics');
  const metric = (name: keyof typeof ICON, value: string, label: string) =>
    el(
      'div',
      'metric',
      `${icon(name)}
       <div><div class="v">${escapeHtml(value)}</div><div class="k">${escapeHtml(label)}</div></div>`,
    );

  if (summary.today_rain !== null) {
    metrics.append(metric('drop', `${nl(summary.today_rain)} mm`, 'neerslag vandaag'));
  }
  if (summary.pressure !== null) {
    // Zonder duizendtalscheiding: "1.020 hPa" leest als een decimaal getal.
    metrics.append(metric('gauge', `${Math.round(summary.pressure)} hPa`, 'luchtdruk'));
  }
  if (summary.today.min !== null) {
    const at = formatTime(summary.today.minAt);
    metrics.append(metric('low', `${temp(summary.today.min)} °C`, `minimum${at ? ` om ${at}` : ''}`));
  }
  if (metrics.children.length > 0) col.append(metrics);

  return col;
}

function rightColumn(summary: Summary): HTMLElement {
  const col = el('div', 'hero-cards');

  // Maand tot nu, met twee balkjes die de vergelijking meteen laten zien.
  const { name, average, normal } = summary.month;
  if (average !== null) {
    const scale = Math.max(average, normal ?? 0, 1);
    const card = el('div', 'glass');
    card.innerHTML = `
      <div class="k">${escapeHtml(name.charAt(0).toUpperCase() + name.slice(1))} tot nu</div>
      <div class="v">${temp(average)}<small> °C gemiddeld</small></div>
      <div class="sub">${
        normal === null
          ? 'nog geen referentie'
          : `normaal voor ${escapeHtml(name)}: ${escapeHtml(temp(normal))} °C`
      }</div>
      <div class="bars">
        <div class="row">
          <span class="lbl">${summary.rain.year}</span>
          <span class="track"><span class="fill" style="width:${((average / scale) * 100).toFixed(0)}%;background:var(--g-warm)"></span></span>
        </div>
        ${
          normal === null
            ? ''
            : `<div class="row">
                 <span class="lbl" style="color:var(--ink-3)">norm</span>
                 <span class="track"><span class="fill" style="width:${((normal / scale) * 100).toFixed(0)}%;background:var(--muted)"></span></span>
               </div>`
        }
      </div>`;
    col.append(card);
  }

  const { deltaPct, total, medianToDate } = summary.rain;
  if (total !== null) {
    const card = el('div', 'glass');
    card.innerHTML = `
      <div class="k">Neerslag t.o.v. mediaan</div>
      <div class="v" style="color:var(--rain)">${
        deltaPct === null ? '—' : `${deltaPct >= 0 ? '+' : '−'}${Math.round(Math.abs(deltaPct))} %`
      }</div>
      <div class="sub">${total} mm${medianToDate === null ? '' : ` i.p.v. ${medianToDate} mm`}</div>`;
    col.append(card);
  }

  return col;
}


/* ---------------------------------------------------------------------- *
 *  Verloop van één dag                                                   *
 * ---------------------------------------------------------------------- */

/** Dezelfde datum, `days` dagen verschoven. Loopt vanzelf over maandgrenzen. */
function shiftDay(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  return ymd(new Date(y!, m! - 1, d! + days));
}

/** "vandaag", "gisteren", of anders de weekdag met de datum erbij. */
function whenLabel(date: string, today: string): string {
  if (date === today) return 'vandaag';
  if (date === shiftDay(today, -1)) return 'gisteren';
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, m! - 1, d!).toLocaleDateString('nl-BE', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });
}

/**
 * Welke dag de curve toont, over een hertekening van de hero heen.
 *
 * De hero wordt in zijn geheel vervangen zodra de live-meting binnen is (zie
 * main.ts). Stond de curve op gisteren, dan zou hij zonder dit geheugen midden
 * in het kijken terugspringen naar vandaag.
 */
let selectedDay: string | null = null;

/**
 * De dag die de curve op dit moment tekent.
 *
 * De deelknop maakt dezelfde dag: sta je op gisteren, dan gaat gisteren mee de
 * afbeelding in. Null zolang er geen curve staat — dan deelt de kaart alleen de
 * cijfers van vandaag.
 */
let shownDay: DayDetail | null = null;

/** De dag die de curve nu toont; null als er geen curve is. */
export function currentDay(): DayDetail | null {
  return shownDay;
}

/** De dagvelden uit de samenvatting, in dezelfde vorm als /day ze levert. */
function fromSummary(summary: Summary): DayDetail {
  return {
    date: summary.today.date ?? ymd(new Date()),
    min: summary.today.min,
    max: summary.today.max,
    minAt: summary.today.minAt,
    maxAt: summary.today.maxAt,
    sun: summary.sun,
    intraday: summary.intraday,
    intradayRain: summary.intradayRain,
    rainTotal: summary.today_rain,
    rainDayTotal: summary.today_rain_day,
    intradayFrom: summary.intradayFrom,
  };
}

/**
 * Het neerslagstaartje voor de kop boven de curve.
 *
 * Null en nul zijn hier niet hetzelfde. Null is geen regenmeter of geen meting,
 * en dan zwijgen we: "droog" zou een bewering zijn over een sensor die er niet
 * is. Nul is een dag die gemeten is en droog bleef, en dat is wél iets om te
 * zeggen.
 *
 * Het dagtotaal staat op de plek waar eerst "neerslag per half uur" stond. Die
 * zin was een legenda voor de staafjes en geen cijfer; het totaal zegt óók dát
 * het regende en draagt er een getal bij, zonder de kop langer te maken — en
 * die kop deelt zijn regel met twee navigatieknoppen. Hoe fijn de staafjes zijn
 * leest wie erop wijst: elk staafje draagt zijn eigen tijdstip en millimeters.
 *
 * Achter elk getal staat het venster waarover geteld is, en er staan er twee.
 * Het eerste loopt van lokale middernacht tot middernacht — zo aggregeert
 * Netatmo zelf en zo staat het in de rest van de site. Het tweede is de
 * meteorologische regendag, 07:00 de dag voordien tot 07:00, waarmee
 * weerdiensten werken; een nachtelijke bui zit daardoor in de andere dag, en het
 * verschil tussen de twee getallen is soms de hele dag. Zonder de vensters erbij
 * is geen van beide na te rekenen.
 *
 * Het tweede getal ontbreekt zodra de halfuurreeks niet meer tot 07:00 van de
 * dag ervoor terugreikt — na acht dagen dus. Dan zwijgen we erover in plaats van
 * een half opgeteld venster te tonen.
 *
 * "droog" draagt zijn venster alleen als er een tweede getal achter komt. Staat
 * het er alleen, dan is er niets om het van te onderscheiden en zou "(00:00–24:00)"
 * de kop met precisie belasten die niemand nodig heeft; komt de regendag erachter,
 * dan is het venster juist het enige dat "droog · 18,1 mm" van een tegenspraak
 * onderscheidt.
 */
function rainPhrase(total: number | null, heaviest: string, rainDayShown: boolean): string {
  if (total === null) return '';
  if (total === 0) return rainDayShown ? ' · droog (00:00–24:00)' : ' · droog';
  return ` · ${nl(total)} mm neerslag (00:00–24:00)${heaviest}`;
}

/**
 * Het tweede totaal, over de regendag van 07:00 tot 07:00.
 *
 * Apart van `rainPhrase` omdat het apart wordt gezet: cursief en een tint
 * flauwer dan de rest van de kop. Het is een voetnoot bij het getal ervoor en
 * geen tweede mededeling — wie het niet zoekt, hoeft er niet over te struikelen.
 *
 * Leeg zodra het kalendertotaal zwijgt (geen meter, geen meting: dan valt er
 * over geen enkel venster iets te zeggen) of zodra de halfuurreeks niet ver
 * genoeg terugreikt.
 *
 * Een droge kalenderdag is géén reden om te zwijgen, en dat was hier eerst wel
 * zo. Een ochtend na een natte avond meet 0 mm sinds middernacht terwijl de
 * regendag er 18 draagt: precies daar lopen de twee vensters het verst uiteen,
 * dus precies daar is het tweede getal het meest waard. Alleen als beide
 * vensters droog zijn valt er niets toe te voegen — dan blijft het bij "droog".
 */
function rainDayPhrase(total: number | null, rainDay: number | null): string {
  if (total === null || rainDay === null) return '';
  if (total === 0 && rainDay === 0) return '';
  return ` · ${nl(rainDay)} mm (07:00–07:00)`;
}

/** Rond pijltje in dezelfde maat als de deelknop ernaast. */
function navButton(glyph: string, label: string): HTMLButtonElement {
  const button = el('button', 'btn-ghost btn-step', `<span aria-hidden="true">${glyph}</span>`);
  button.type = 'button';
  button.title = label;
  button.setAttribute('aria-label', label);
  return button;
}

/**
 * De curve met twee pijltjes erboven om per dag terug te bladeren.
 *
 * Alleen de curve navigeert, niet de hele hero: de grote temperatuur, de
 * metrics en de kaarten ernaast gaan over "nu" en zouden bij gisteren een
 * verwarrend soort halfheid krijgen. De grens ligt bij `intradayFrom` — de
 * ruwe reeks wordt na acht dagen gesnoeid, en verder terug valt er niets meer
 * per vijf minuten te tekenen.
 */
function curveCard(summary: Summary): HTMLElement | null {
  const initial = fromSummary(summary);
  const today = initial.date;

  // Geen reeks van vandaag én geen archief: dan valt er niets te tonen en ook
  // niets te bladeren.
  if (initial.intraday.length < 5 && initial.intradayFrom === null) {
    shownDay = null;
    return null;
  }

  const wrap = el('div', 'today');
  const head = el('div', 'today-head');
  const heading = el('div', 'k');
  const nav = el('div', 'today-nav');
  const back = navButton('‹', 'Vorige dag');
  const forward = navButton('›', 'Volgende dag');
  nav.append(back, forward);
  head.append(heading, nav);

  const plot = el('div', 'today-plot');
  wrap.append(head, plot);

  let current = initial;
  let pending = false;

  const draw = (): void => {
    shownDay = current;
    const curve = drawCurve(plot, current);
    const when = whenLabel(current.date, today);
    // De regendag wordt eerst opgemaakt: de zin ervoor moet weten of er nog een
    // getal achter komt, want dat bepaalt of "droog" zijn venster meedraagt.
    const rainDay = rainDayPhrase(current.rainTotal, current.rainDayTotal);
    // Het dagtotaal hangt niet aan de curve: het komt uit het dagaggregaat en
    // staat er dus ook nog als de ruwe reeks van die dag gesnoeid is. Vaak is
    // het dan het enige wat we van die dag nog weten.
    const lead =
      `Verloop ${when}` +
      (curve === null ? ' · geen metingen bewaard' : ' · meting per 5 minuten') +
      rainPhrase(current.rainTotal, curve?.heaviest ?? '', rainDay !== '');
    heading.replaceChildren(document.createTextNode(lead));

    if (rainDay !== '') {
      const aside = el('span', 'rain-day');
      aside.textContent = rainDay;
      heading.append(aside);
    }

    const first = current.intradayFrom;
    back.disabled = pending || (first !== null && current.date <= first);
    forward.disabled = pending || current.date >= today;
  };

  const go = async (date: string): Promise<void> => {
    if (pending) return;
    pending = true;
    back.disabled = true;
    forward.disabled = true;
    wrap.setAttribute('aria-busy', 'true');
    try {
      current = await api.day(date);
    } catch {
      // Mislukt de ophaling, dan blijft staan wat er stond.
    } finally {
      pending = false;
      selectedDay = current.date;
      wrap.removeAttribute('aria-busy');
      draw();
    }
  };

  back.addEventListener('click', () => void go(shiftDay(current.date, -1)));
  forward.addEventListener('click', () => void go(shiftDay(current.date, 1)));

  draw();
  // Is de hero opnieuw opgebouwd terwijl je een oudere dag bekeek, haal die dan
  // meteen terug — de curve van vandaag staat er intussen als tussenstand.
  if (selectedDay !== null && selectedDay !== current.date) void go(selectedDay);

  return wrap;
}

/**
 * Verloop van één dag, met de hand als SVG.
 *
 * Bewust geen Chart.js: dit is een sfeerlijn van één haarbreedte met vier vaste
 * markeringen (zonop, zononder, dagminimum, dagmaximum) en de buien van die dag
 * als staafjes langs de bodem. Een grafiekbibliotheek zou hier alleen assen en
 * interactie bijzetten die niemand nodig heeft.
 *
 * @returns `heaviest` voor de kop — het staartje over de zwaarste bui, leeg als
 *   dat label in het plotvlak zelf paste — of null als er voor deze dag geen
 *   ruwe reeks meer is.
 */
function drawCurve(plot: HTMLElement, day: DayDetail): { heaviest: string } | null {
  const points = day.intraday;
  if (points.length < 5) {
    plot.replaceChildren(el('div', 'today-empty', 'Geen ruwe metingen voor deze dag.'));
    return null;
  }

  const wet = day.intradayRain.filter((b) => b.value > 0);

  const [yy, mm, dd] = day.date.split('-').map(Number);
  const dayStart = Math.floor(new Date(yy!, mm! - 1, dd!).getTime() / 1000);
  // Niet dayStart + 86 400: op de dag van de klokwissel duurt de dag 23 of 25
  // uur, en dan zou de curve scheef in het vlak liggen.
  const dayLength = Math.floor(new Date(yy!, mm! - 1, dd! + 1).getTime() / 1000) - dayStart;

  /*
   * De schaal moet ook de dagextremen bevatten, niet alleen de reeks.
   *
   * Meestal is dat hetzelfde: de server leidt het dagminimum en -maximum uit
   * precies deze punten af. Maar het dagaggregaat kan een ruimere dag beslaan
   * dan wat er van de vijfminutenreeks bewaard is — na een herstart, of als een
   * intraday-ophaling een gat liet. Dan valt het extreem buiten [lo, hi], komt
   * de stip boven of onder het plotvlak terecht en blijft alleen het label in
   * de lucht hangen. Ze meerekenen kost niets en houdt de stip binnen beeld.
   */
  const values = points.map((p) => p.value);
  const marked = [day.min, day.max].filter((v): v is number => v !== null);
  const lo = Math.min(...values, ...marked);
  const hi = Math.max(...values, ...marked);
  const span = Math.max(0.5, hi - lo);

  const X = (ts: number) => ((ts - dayStart) / dayLength) * 1000;
  const Y = (v: number) => 80 - ((v - lo) / span) * 62;

  const path = points
    .map((p, i) => (i ? 'L' : 'M') + X(p.ts).toFixed(1) + ' ' + Y(p.value).toFixed(1))
    .join(' ');

  /*
   * De markeringen zijn HTML en geen SVG, en dat is geen smaakkwestie.
   *
   * De curve staat in een SVG met `preserveAspectRatio="none"`: die rekt alleen
   * de x-as en laat y op de pixel staan. Een tweede SVG met dezelfde viewBox
   * doet dat niet — die schaalt uniform (anders werden de stippen ovaal) en zet
   * wat overblijft in het midden. Gemeten bij een plotvlak van 328 px: de
   * stippenlaag kreeg y-schaal 0,328 en een offset van 32 px, waardoor een stip
   * op y=80 op 58 px belandde en er dus 21 px boven de lijn zweefde. Op een
   * breed scherm gebeurt hetzelfde horizontaal (84 px bij 1168 px breed).
   *
   * Als absoluut geplaatst element klopt het wel: x is een percentage van de
   * breedte — precies wat de gerekte SVG met de x-as doet — en y is een pixel,
   * want het plotvlak is even hoog als de viewBox. Zo staan ze in dezelfde
   * ruimte als de labels, die altijd al zo geplaatst werden.
   */
  const marks: HTMLElement[] = [];
  const mark = (className: string, x: number, y: number): HTMLElement => {
    const node = el('span', className);
    node.style.left = `${(x / 10).toFixed(2)}%`;
    node.style.top = `${y.toFixed(1)}px`;
    return node;
  };

  // Zonop en zononder als verticale streepjes tot de bodem.
  const sunLabels: HTMLElement[] = [];
  for (const [key, ts, colour, label] of [
    ['sunrise', day.sun.sunrise, 'var(--amber)', 'zon op'],
    ['sunset', day.sun.sunset, 'var(--ink-3)', 'zon onder'],
  ] as const) {
    if (!ts) continue;
    const x = X(ts);
    if (x < 0 || x > 1000) continue;
    // Hoogte van de lijn volgt de curve op dat tijdstip, of de bodem als de
    // meting nog niet zover is.
    const nearest = points.reduce((best, p) =>
      Math.abs(p.ts - ts) < Math.abs(best.ts - ts) ? p : best,
    );
    const y = Y(nearest.value);

    const line = mark('sun-line', x, y);
    line.style.height = `${(96 - y).toFixed(1)}px`;
    const knob = mark('sun-dot', x, y);
    knob.style.borderColor = colour;
    marks.push(line, knob);

    const el2 = el('span', 'sun-label', `${label} ${escapeHtml(formatTime(ts) ?? '')}`);
    el2.style.left = `${(x / 10).toFixed(1)}%`;
    el2.dataset.key = key;
    sunLabels.push(el2);
  }

  // Dagextremen als gekleurde stippen met een label.
  const dotLabels: HTMLElement[] = [];
  for (const [iso, value, colour, above] of [
    [day.minAt, day.min, 'var(--violet)', false],
    [day.maxAt, day.max, 'var(--coral)', true],
  ] as const) {
    if (!iso || value === null) continue;
    const ts = Math.floor(new Date(iso).getTime() / 1000);
    const x = X(ts);
    if (x < 0 || x > 1000) continue;
    const y = Y(value);
    const dot = mark('curve-dot', x, y);
    dot.style.background = colour;
    marks.push(dot);

    const label = el(
      'span',
      'dot-label',
      `${escapeHtml(formatTime(iso) ?? '')} · ${escapeHtml(temp(value))} °C`,
    );
    // Boven de stip, maar niet buiten het plotvlak: ligt het extreem hoog in de
    // grafiek — zoals wanneer het maximum vlak na middernacht viel — dan zou het
    // label over de kop erboven vallen. Dan wisselt het naar de onderkant.
    //
    // De 14 hieronder hangt samen met de tickrij: die begint in styles.css op
    // 116, net onder de laagst mogelijke onderkant van dit label (y is hoogstens
    // 80, de bodem van de band). Wie aan deze marge of aan Y() komt, verschuift
    // die rijen mee.
    const wantsAbove = above && y > 30;
    label.style.top = `${(wantsAbove ? y - 26 : y + 14).toFixed(0)}px`;
    // Aan de randen zou een gecentreerd label half buiten de kaart hangen.
    const pct = x / 10;
    label.style.left = `${Math.min(92, Math.max(8, pct)).toFixed(1)}%`;
    dotLabels.push(label);
  }

  /*
   * Neerslag als staafjes langs de bodem, in het gerekte vlak.
   *
   * Ze horen bij de curve-SVG en niet bij die van de stippen: `preserveAspect
   * Ratio="none"` rekt daar alleen de x-as, zodat een halfuur precies onder het
   * juiste stuk curve valt en een hoogte in gebruikers-eenheden ook echt die
   * pixels is. In de andere SVG zou een staafje schuin naast zijn bui belanden.
   */
  const rainBars: string[] = [];
  const MAX_BAR = 18; // hoger komt het staafje in het bereik van de curve
  const MIN_BAR = 2.5; // 0,1 mm moet een staafje zijn, geen haartje
  // Vaste referentie in plaats van de dagpiek: anders ziet een dag met drie
  // druppels er even nat uit als een onweersbui. Regent het harder dan de
  // referentie, dan schuift de schaal wél mee zodat alles binnen het vlak past.
  const peak = wet.reduce<{ ts: number; value: number } | null>(
    (best, b) => (best === null || b.value > best.value ? b : best),
    null,
  );
  const full = Math.max(2, peak?.value ?? 0);
  const barHeight = (mmRain: number) => MIN_BAR + (Math.min(mmRain, full) / full) * (MAX_BAR - MIN_BAR);

  for (const bucket of wet) {
    const x = X(bucket.ts);
    const width = X(bucket.ts + 1800) - x;
    if (x + width < 0 || x > 1000) continue;
    const h = barHeight(bucket.value);
    rainBars.push(
      `<rect x="${(x + 3).toFixed(1)}" y="${(96 - h).toFixed(1)}" width="${(width - 6).toFixed(1)}"
         height="${h.toFixed(1)}" style="fill:var(--rain)" fill-opacity="0.55"><title>${escapeHtml(
           formatTime(bucket.ts) ?? '',
         )} · ${escapeHtml(nl(bucket.value))} mm</title></rect>`,
    );
  }

  /*
   * Boven de zwaarste bui het tijdstip en de hoeveelheid, in dezelfde vorm als
   * de labels bij de dagextremen. Alleen als de curve daar hoog genoeg loopt:
   * regent het op het koudste moment van de dag, dan is er geen plaats en zou
   * het label dwars door de lijn lopen. Dan gaat hetzelfde getal naar de kop —
   * hoe dan ook staat er ergens hoeveel er in de zwaarste bui viel.
   */
  const rainLabels: HTMLElement[] = [];
  let heaviest = '';
  if (peak) {
    const top = 96 - barHeight(peak.value) - 17;
    const nearest = points.reduce((best, p) =>
      Math.abs(p.ts - peak.ts) < Math.abs(best.ts - peak.ts) ? p : best,
    );
    const x = X(peak.ts + 900);
    const at = formatTime(peak.ts) ?? '';
    const amount = nl(peak.value);

    if (Y(nearest.value) < top - 6 && x >= 0 && x <= 1000) {
      const label = el('span', 'rain-label', `${escapeHtml(at)} · ${escapeHtml(amount)} mm`);
      label.style.top = `${top.toFixed(0)}px`;
      label.style.left = `${Math.min(92, Math.max(8, x / 10)).toFixed(1)}%`;
      rainLabels.push(label);
    } else {
      heaviest = `, meeste ${amount} mm om ${at}`;
    }
  }

  plot.innerHTML = `
    <svg viewBox="0 0 1000 96" preserveAspectRatio="none" aria-hidden="true" style="height:96px">
      ${rainBars.join('')}
      <path d="${path}" fill="none" style="stroke:var(--curve)" stroke-width="1.4" vector-effect="non-scaling-stroke"/>
    </svg>`;

  for (const hour of [0, 12, 24]) {
    const tick = el('span', 'tick', `${String(hour).padStart(2, '0')}:00`);
    tick.style.left = `${((hour / 24) * 100).toFixed(1)}%`;
    plot.append(tick);
  }
  plot.append(...marks, ...sunLabels, ...dotLabels, ...rainLabels);

  return { heaviest };
}
